import { useState, useEffect } from 'react';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Calendar, 
  Scissors, 
  Image, 
  User, 
  LogOut, 
  Menu,
  X,
  FileEdit,
  Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Toaster } from '../components/ui/sonner';

const sidebarItems = [
  { path: '/admin/dashboard', label: 'Appointments', icon: Calendar },
  { path: '/admin/schedule', label: 'Schedule', icon: Clock },
  { path: '/admin/services', label: 'Services', icon: Scissors },
  { path: '/admin/gallery', label: 'Gallery', icon: Image },
  { path: '/admin/content', label: 'Content Editor', icon: FileEdit },
  { path: '/admin/profile', label: 'Profile & Bio', icon: User },
];

export default function DashboardLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin');
    }
  }, [user, loading, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9C6A48]"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Toaster position="top-center" richColors />
      
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E3DACD] px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2">
          <Menu className="w-6 h-6 text-[#2B2623]" />
        </button>
        <h1 
          className="text-xl font-light text-[#2B2623]"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          BraidsbyAbbee
        </h1>
        <div className="w-10"></div>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white border-r border-[#E3DACD] z-50
        transform transition-transform duration-300
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6 border-b border-[#E3DACD] flex items-center justify-between">
          <Link 
            to="/"
            className="text-xl font-light text-[#2B2623]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            BraidsbyAbbee
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1"
          >
            <X className="w-5 h-5 text-[#756A63]" />
          </button>
        </div>

        <nav className="p-4 space-y-2">
          {sidebarItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all sidebar-item
                  ${isActive 
                    ? 'bg-[#9C6A48]/10 text-[#9C6A48] border-r-2 border-[#9C6A48]' 
                    : 'text-[#756A63] hover:bg-[#9C6A48]/5 hover:text-[#9C6A48]'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E3DACD]">
          <div className="flex items-center gap-3 px-4 py-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#9C6A48] flex items-center justify-center text-white text-sm">
              {user.name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#2B2623] truncate">{user.name}</p>
              <p className="text-xs text-[#756A63] truncate">{user.email}</p>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={handleLogout}
            variant="outline"
            className="w-full rounded-xl border-[#E3DACD] text-[#756A63] hover:bg-[#9C6A48]/5 hover:text-[#9C6A48]"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
